/**
 * Sentry Configuration for Frontend
 * 
 * Sentry provides:
 * - Automatic error capturing with stack traces
 * - User context (which agent experienced the issue)
 * - Session replay (see what users did before error)
 * - Performance monitoring
 * - Breadcrumbs (events leading up to error)
 * 
 * To set up Sentry:
 * 1. Install: npm install @sentry/react
 * 2. Go to https://sentry.io
 * 3. Create a project (React)
 * 4. Copy the DSN and set VITE_SENTRY_DSN environment variable
 */

import React from 'react';
import * as Sentry from '@sentry/react';
import { isAuth401Error } from '../shared/utils/apiErrors';

// Check if Sentry is configured
// Support separate DSNs for dev/prod OR single DSN with environment tags
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || 
  (import.meta.env.MODE === 'production' 
    ? import.meta.env.VITE_SENTRY_DSN_PROD 
    : import.meta.env.VITE_SENTRY_DSN_DEV);
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
const IS_SENTRY_ENABLED = !!SENTRY_DSN;

// Severity levels compatible with Sentry
export type SeverityLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';

/**
 * Initialize Sentry - call this in main.tsx BEFORE React.render()
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    console.log('⚠️ Sentry DSN not configured. Set VITE_SENTRY_DSN environment variable to enable.');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false, // Don't mask text for debugging
        blockAllMedia: false, // Don't block media
      }),
    ],

    // Performance monitoring - sample 10% in production, 100% in dev
    tracesSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.1 : 1.0,

    // Session replay - capture 10% of sessions, 100% with errors
    replaysSessionSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Handle chunk load errors specially
    beforeSend(event: any, hint: any) {
      const original = hint?.originalException;
      const exception = event.exception?.values?.[0];

      if (
        isAuth401Error(original) ||
        (exception?.type === 'AxiosError' && exception.value?.includes('401'))
      ) {
        return null;
      }
      
      if (exception) {
        const type = exception.type?.toLowerCase() || '';
        const message = exception.value?.toLowerCase() || '';
        
        // Detect chunk load errors
        const isChunkLoadError = 
          type.includes('chunkloaderror') ||
          message.includes('failed to fetch dynamically imported module') ||
          message.includes('loading chunk') ||
          message.includes('loading css chunk') ||
          message.includes('failed to load module script');
        
        if (isChunkLoadError) {
          // Downgrade to WARNING level (not ERROR) so it doesn't trigger alerts
          // but still gets logged for monitoring trends
          event.level = 'warning';
          
          // Add context to help distinguish deployment vs real issues
          event.tags = event.tags || {};
          event.tags.chunk_load_error = 'true';
          event.tags.likely_cause = 'deployment_or_network';
          
          // Add fingerprint so all chunk errors group together
          // This prevents 100 users refreshing from creating 100 separate issues
          event.fingerprint = ['chunk-load-error'];
          
          console.log('[Sentry] Chunk load error logged as warning:', message.substring(0, 80));
          
          return event; // Still send, but as warning
        }
      }
      
      return event;
    },

    // Add tags to all events
    initialScope: {
      tags: {
        app: 'cwy-admin-web',
      },
    },
  });

  console.log(`✅ Sentry frontend initialized (environment: ${SENTRY_ENVIRONMENT})`);
}

/**
 * Set user context for Sentry events
 * Call this after authentication
 */
export function setSentryUser(userId: string, email?: string, username?: string): void {
  if (!IS_SENTRY_ENABLED) return;
  
  Sentry.setUser({
    id: userId,
    email,
    username,
  });
}

/**
 * Clear user context on logout
 */
export function clearSentryUser(): void {
  if (!IS_SENTRY_ENABLED) return;
  Sentry.setUser(null);
}

/**
 * Add breadcrumb (event leading up to error)
 */
export function addSentryBreadcrumb(
  message: string,
  category: string,
  level: SeverityLevel = 'info',
  data?: Record<string, unknown>
): void {
  if (!IS_SENTRY_ENABLED) return;
  
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error | unknown, context?: Record<string, unknown>): void {
  if (!IS_SENTRY_ENABLED) {
    console.error('Error (Sentry disabled):', error);
    return;
  }
  
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message manually
 */
export function captureMessage(
  message: string, 
  level: SeverityLevel = 'info',
  context?: Record<string, unknown>
): void {
  if (!IS_SENTRY_ENABLED) return;
  
  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

/**
 * Set a tag on all future events
 */
export function setSentryTag(key: string, value: string): void {
  if (!IS_SENTRY_ENABLED) return;
  Sentry.setTag(key, value);
}

/**
 * Set call context for debugging
 * Call this when a call starts
 */
export function setCallContext(callSid: string, clientPhone?: string, clientName?: string): void {
  if (!IS_SENTRY_ENABLED) return;
  
  Sentry.setTag('call_sid', callSid);
  Sentry.setExtra('call_context', {
    callSid,
    clientPhone,
    clientName,
    startedAt: new Date().toISOString(),
  });
  
  addSentryBreadcrumb('Call started', 'call', 'info', {
    callSid,
    clientPhone,
    clientName,
  });
}

/**
 * Clear call context when call ends
 */
export function clearCallContext(): void {
  if (!IS_SENTRY_ENABLED) return;
  
  Sentry.setTag('call_sid', undefined);
  Sentry.setExtra('call_context', undefined);
  
  addSentryBreadcrumb('Call ended', 'call', 'info');
}

/**
 * Sentry Error Boundary component for React
 * Wrap your app with this to catch React errors
 */
export const SentryErrorBoundary = Sentry?.ErrorBoundary || (({ children }: { children: React.ReactNode }) => children);

/**
 * HOC to wrap components with error boundary
 */
export const withSentryErrorBoundary = Sentry?.withErrorBoundary || ((component: any) => component);

/**
 * React profiler for performance monitoring
 */
export const SentryProfiler = Sentry?.withProfiler || ((component: any) => component);

// Export Sentry for direct access if needed
export { Sentry };
export const isSentryEnabled = IS_SENTRY_ENABLED;

